import { useTranslation } from 'react-i18next';
// VehicleOperatorsButton.jsx
//
// Icono compacto en el panel de control que abre el panel de operadores del
// vehículo. La lógica vive en VehicleOperatorsPanel para reutilizarla también
// en el tab "Operadores" del detalle del vehículo.
//
// Contenedor:
//   - Escritorio → Popover anclado al botón, con alto acotado y scroll interno.
//     El alto fijo evita que MUI tenga que empujarlo hacia arriba para que
//     quepa (lo que hacía que "se fuera para arriba" y tapara la pantalla).
//   - Móvil/tablet → Diálogo centrado, que es más usable que un popover.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Popover,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Badge as BadgeIcon } from '@mui/icons-material';

import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import VehicleOperatorsPanel from './VehicleOperatorsPanel';

// Alto máximo del popover: deja margen arriba y abajo para que nunca quede
// pegado al navbar ni se corte contra el borde inferior.
const PANEL_MAX_HEIGHT = 'min(70vh, 560px)';

const VehicleOperatorsButton = ({ vehicleId, size = 'small', sx = {} }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('manager');

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null); // { assigned_count, max_operators }

  // Solo el contador, para colorear el icono sin abrir el panel.
  useEffect(() => {
    let cancel = false;
    if (!vehicleId) return undefined;
    api.operators.vehicleSummary(vehicleId)
      .then((r) => { if (!cancel) setSummary(r.data); })
      .catch(() => { if (!cancel) setSummary(null); });
    return () => { cancel = true; };
  }, [vehicleId]);

  const count = summary?.assigned_count ?? null;
  const max = summary?.max_operators ?? 1;

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  // Identidad estable y sin escritura inutil: `setSummary` con un objeto nuevo
  // en cada llamada re-renderizaba siempre, y eso volvia a recrear el callback
  // del panel, que recargaba en bucle. Se conserva el estado si el conteo no
  // cambio.
  const handleAssignmentsChanged = useCallback((list) => {
    const assignedCount = Array.isArray(list) ? list.length : 0;
    setSummary((prev) => {
      if (prev && prev.assigned_count === assignedCount) return prev;
      return { ...(prev || { max_operators: max }), assigned_count: assignedCount };
    });
  }, [max]);

  const tooltipTxt = count === null
    ? t('hardcoded.components_operators_vehicleoperatorsbutton.operadores_del_vehiculo')
    : count > 0 ? t('hardcoded.components_operators_vehicleoperatorsbutton.operadores_asignados_countmax', { count: count, max: max }) : t('hardcoded.components_operators_vehicleoperatorsbutton.sin_operadores_asignados');

  const panel = (
    <VehicleOperatorsPanel
      vehicleId={vehicleId}
      canEdit={canEdit}
      compact
      onClose={handleClose}
      onAssignmentsChanged={handleAssignmentsChanged}
      maxOperators={summary?.max_operators}
    />
  );

  return (
    <>
      <Tooltip title={tooltipTxt}>
        <IconButton
          size={size}
          onClick={handleOpen}
          sx={{ color: count > 0 ? 'primary.main' : 'text.disabled', ...sx }}
        >
          <BadgeIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>

      {isSmall ? (
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="xs"
          scroll="paper"
          slotProps={{ paper: { sx: { m: 2, borderRadius: 2 } } }}
        >
          <DialogContent sx={{ p: 2 }}>{open && panel}</DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // marginThreshold mantiene el panel dentro del viewport; el alto
          // acotado + scroll interno evitan reposicionamientos bruscos.
          marginThreshold={16}
          slotProps={{
            paper: {
              sx: {
                width: 380,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: PANEL_MAX_HEIGHT,
                overflowY: 'auto',
                p: 1.5,
                borderRadius: 2,
              },
            },
          }}
        >
          {open && panel}
        </Popover>
      )}
    </>
  );
};

export default VehicleOperatorsButton;
